import Spinner from '@clearkit/icons/indicators/Spinner';
import { CKButton, CKField, CKTextField, CKTooltip } from '@clearkit/react';
import isEqual from 'lodash/isEqual';

import { ConfirmDialogBase } from '~/components/ConfirmDialog';
import Footer from '~/components/Footer';
import SearchBuilder, { validateSearch } from '~/components/SearchBuilder';
import { hasError } from '~/lib/graphql';
import history from '~/lib/history';

import CollectionSelect from './CollectionSelect';
import styles from './styles';

const placeholderValue = '';

class SegmentForm extends React.Component {
  state = {
    input: this.props.input,
    isDirtyInput: false,
    isDirtyInputDialogOpen: false,
    nextPath: null,
  };

  componentWillUnmount() {
    this.unblockHistory();
  }

  isValid() {
    const { input } = this.state;
    const searchValid = validateSearch(input.search).length === 0;

    return input.name && input.collectionId && searchValid;
  }

  blockHistory = () => {
    if (this._unblockHistory) {
      return;
    }

    this._unblockHistory = history.block((event) => {
      const { isDirtyInput } = this.state;

      if (!isDirtyInput) {
        return true;
      }

      this.setState({
        isDirtyInputDialogOpen: true,
        nextPath: event.pathname,
      });

      return false;
    });
  };

  unblockHistory = () => {
    if (this._unblockHistory) {
      this._unblockHistory();
    }

    this._unblockHistory = null;
  };

  cancelDirtyInputDialog = () => {
    this.setState({ isDirtyInputDialogOpen: false });
  };

  confirmDirtyInputDialog = () => {
    this.unblockHistory();

    const { nextPath } = this.state;

    if (nextPath) {
      history.push(nextPath);
    }
  };

  updateInput = (fields) => {
    this.setState(
      ({ input }) => ({ input: { ...input, ...fields } }),
      this.blockHistory,
    );
  };

  updateSearch = (fields) => {
    this.setState(
      ({ input }) => ({
        input: {
          ...input,
          ...fields,
        },
        isDirtyInput: !isEqual(fields.search, this.state.input.search),
      }),
      this.blockHistory,
    );
  };

  handleSubmit = () => {
    this.unblockHistory();

    const { onSubmit } = this.props;
    const { input } = this.state;

    onSubmit(input);
  };

  renderSaveButton = () => {
    const { loading, newRecord } = this.props;

    return (
      <CKButton
        isDisabled={loading || !this.isValid()}
        onClick={this.handleSubmit}
        type="submit"
        variant="bold"
        variantColor="green">
        {newRecord ? 'Create audience' : 'Save audience changes'}
      </CKButton>
    );
  };

  render() {
    const {
      type,
      classes,
      segment,
      collections,
      errors,
      loading,
      secondaryAction,
      attributes,
      salesforceUsers,
    } = this.props;

    const {
      input: { collectionId = placeholderValue },
      input: { name = placeholderValue },
      input: { search },
      isDirtyInputDialogOpen,
    } = this.state;

    const searchValid = validateSearch(search).length === 0;

    return (
      <>
        <div className="flex mx-10 mt-4 mb-6">
          <div className="flex-1 mr-4" rel="segment-name">
            <label htmlFor="segment-name" className="inline-block mb-3 text-sm">
              <span>Audience name*</span>
            </label>
            <CKField
              className="w-full"
              variant={hasError(errors, 'name') && 'error'}>
              <CKTextField
                isRequired
                value={name}
                className="w-full"
                aria-label="Audience Name"
                placeholder="Enter name"
                id="segment-name"
                onChange={(event) => {
                  this.updateInput({ name: event.target.value });
                }}
              />
            </CKField>
          </div>
          <div className="flex-1" rel="segment-collection">
            <label
              htmlFor="collection-toggle-button"
              className="inline-block mb-3 text-sm">
              <span>Collection*</span>
            </label>
            <CollectionSelect
              value={collectionId}
              collections={collections}
              onChange={(collectionId) => this.updateInput({ collectionId })}
              error={hasError(errors, 'collectionId')}
            />
          </div>
        </div>

        <div className={classes.searchBuilderContainer}>
          <SearchBuilder
            disabled={segment?.searchDependencyErrors}
            showInvalidFields={this.state.showInvalidFields}
            type={type}
            search={search}
            attributes={attributes}
            salesforceUsers={salesforceUsers}
            onChange={(search) => {
              this.updateSearch({ search });
            }}
          />
        </div>

        <Footer
          left={secondaryAction}
          right={
            <>
              {loading && <Spinner width={32} />}
              <div
                onMouseEnter={() => this.setState({ showInvalidFields: true })}
                onMouseLeave={() =>
                  this.setState({ showInvalidFields: false })
                }>
                {searchValid ? (
                  this.renderSaveButton()
                ) : (
                  <CKTooltip
                    unstable_portal={false}
                    tooltip={<p className="m-2">Some fields are invalid</p>}
                    placement="top">
                    <div>{this.renderSaveButton()}</div>
                  </CKTooltip>
                )}
              </div>
            </>
          }
        />

        <ConfirmDialogBase
          confirmText="Discard changes"
          message="Navigating away will cause you to lose your changes."
          onCancel={this.cancelDirtyInputDialog}
          onConfirm={this.confirmDirtyInputDialog}
          open={isDirtyInputDialogOpen}
          title="Are you sure?"
        />
      </>
    );
  }
}

SegmentForm.propTypes = {
  newRecord: PropTypes.bool,
  input: PropTypes.shape({
    collectionId: PropTypes.string,
    name: PropTypes.string,
    search: PropTypes.object,
  }).isRequired,
  attributes: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
  onSubmit: PropTypes.func,
  classes: PropTypes.object,
  loading: PropTypes.bool,
  type: PropTypes.string,
  collections: PropTypes.array,
  errors: PropTypes.array,
  secondaryAction: PropTypes.node,
};

export default withStyles(styles)(SegmentForm);
